import { Icon } from '@/components/Icon';
import colors from '@/config/colors';
import { useState } from 'react';
import {
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  Path,
} from 'react-hook-form';
import { Platform, Pressable, TextInput, View } from 'react-native';
import { Text } from '@/components/Text';

type PasswordInputProps<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
> = {
  field: ControllerRenderProps<TFieldValues, TName>;
  fieldState: ControllerFieldState;
  placeholder?: string;
};

export const PasswordInput = <
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>({
  field,
  fieldState,
  placeholder = 'Passwort',
}: PasswordInputProps<TFieldValues, TName>) => {
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const { value, onChange, onBlur } = field;
  const { error } = fieldState;

  const toggleShowPassword = () => {
    setShowPassword((prevState) => !prevState);
  };

  const borderColor = error ? 'border-red-600' : 'border-green-neolexon';
  const textColor = error ? 'text-red-700' : 'text-green-neolexon';

  return (
    <View className="flex-col gap-1">
      <View
        className={`border ${borderColor} rounded-3xl flex-row items-center px-3`}
      >
        <Icon name="key" className={`${textColor} text-[28px]`} />
        <TextInput
          id="pwd-input"
          className="flex-1 p-3 text-lg font-medium"
          secureTextEntry={!showPassword}
          placeholder={placeholder}
          placeholderTextColor={colors.twGrey}
          autoComplete="off"
          autoCapitalize="none"
          onBlur={onBlur}
          textBreakStrategy="simple"
          onChangeText={onChange}
          value={value}
          style={{
            ...(Platform.OS === 'ios'
              ? { lineHeight: 0, color: colors.newUI.therapist.textBlue100 }
              : { color: colors.newUI.therapist.textBlue100 }),
          }}
        />
        <Pressable onPress={toggleShowPassword}>
          <Icon
            name={showPassword ? 'eye' : 'eyeOff'}
            size={28}
            className="text-green-neolexon"
          />
        </Pressable>
      </View>
      <View className="flex-row justify-start items-center">
        <Text className="text-red-700 text-base pl-1">{error?.message}</Text>
      </View>
    </View>
  );
};
